/**
 * GBなび API クライアント (gb-terasu-pulse)
 * LIFF不使用、gbpav74内のWeb画面から呼ぶ。
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;
const getClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export type TerasuPolarity = 'positive' | 'neutral' | 'negative';

/** 多言語テキスト(9言語)。ja は text を使用。未翻訳は null → text へフォールバック。 */
export interface TerasuMultiLang {
  textEn?: string | null; textId?: string | null; textMn?: string | null;
  textNe?: string | null; textVi?: string | null; textAr?: string | null;
  textBn?: string | null; textZh?: string | null; textEs?: string | null;
}

export interface TerasuChoiceDTO extends TerasuMultiLang {
  order: number;
  text: string;
  polarity: TerasuPolarity;
}

export interface TerasuQuestionDTO extends TerasuMultiLang {
  id: string;
  category: 'fixed' | 'theme' | 'followup';
  indicatorKey?: string;
  themeKey?: string;
  parentQuestionId?: string;
  triggerChoiceIndex?: number;
  text: string;
  choices: TerasuChoiceDTO[];
}

export interface WeekQuestionsResponse {
  weekKey: string;
  indicatorKey: string;
  themeKey: string;
  fixed: TerasuQuestionDTO[];
  followups: TerasuQuestionDTO[];
  theme: TerasuQuestionDTO[];
}

export interface IndicatorResult {
  indicatorKey: string;
  label?: string;
  weather: 'sunny' | 'cloudy' | 'rain' | 'storm';
  emoji: string;
  positiveRate: number;
  score: number;
  riskColor: 'green' | 'yellow' | 'red';
  counts: { positive: number; neutral: number; negative: number };
}

export interface WeeklySummary {
  weekKey: string;
  year: number;
  month: number;
  week: number;
  overallWeather: IndicatorResult['weather'] | null;
  overallEmoji: string | null;
  indicators: Record<string, IndicatorResult & { weekKey: string }>;
  theme: IndicatorResult | null;
  danger: Array<{ indicatorKey: string; reasons: string[]; latestWeather: string }>;
  respondedUsers: number;
  memberCount: number;
  responseRate: number;
}

export interface MonthlySummary {
  year: number;
  month: number;
  grid: Record<string, Array<IndicatorResult['weather'] | null>>;
  byDepartment: Array<{ departmentId: string; departmentName?: string; memberCount: number; respondedUsers: number; indicators: Record<string, IndicatorResult> }>;
  themeKey: string;
  theme: Record<string, IndicatorResult>;
  respondedUsers: number;
  memberCount: number;
  responseRate: number;
}

export interface MonthlyAction {
  text: string;
  executionCount: number;
  phase: number;
}

/** 現在日付(または指定日)から weekKey 'YYYY-MM-Wn' を算出 (月内週番号、5週目は4に丸め) */
export function getCurrentWeekKey(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const week = Math.min(4, Math.ceil(d.getDate() / 7));
  return `${year}-${String(month).padStart(2, '0')}-W${week}`;
}

export const terasuApi = {
  async getWeekQuestions(weekKey: string): Promise<WeekQuestionsResponse> {
    const res = await getClient().get<WeekQuestionsResponse>(`/terasu/weeks/${encodeURIComponent(weekKey)}/questions`);
    return res.data;
  },

  async submitResponses(weekKey: string, answers: Array<{ questionId: string; choiceIndex: number }>): Promise<{ saved: number }> {
    const res = await getClient().post(`/terasu/weeks/${encodeURIComponent(weekKey)}/responses`, { answers });
    return res.data;
  },

  async getWeeklySummary(weekKey: string, recompute = false): Promise<{ result: WeeklySummary; cached: boolean }> {
    const res = await getClient().get(`/terasu/summary/weekly`, { params: { weekKey, ...(recompute ? { recompute: 'true' } : {}) } });
    return res.data;
  },

  async getMonthlySummary(year: number, month: number, recompute = false): Promise<{ result: MonthlySummary; cached: boolean }> {
    const res = await getClient().get(`/terasu/summary/monthly`, { params: { year, month, ...(recompute ? { recompute: 'true' } : {}) } });
    return res.data;
  },

  async getMonthlyAction(year: number, month: number): Promise<{ action: MonthlyAction | null }> {
    const res = await getClient().get(`/terasu/actions`, { params: { year, month } });
    return res.data;
  },

  async setMonthlyAction(year: number, month: number, text: string): Promise<{ action: MonthlyAction }> {
    const res = await getClient().put(`/terasu/actions`, { year, month, text });
    return res.data;
  },
};
